import type { PostgrestError } from '@supabase/supabase-js';

function rlsHint(message: string, context?: 'storage' | 'crm' | 'attachment'): string {
  if (context === 'attachment' || /task_attachments|crm-task-attachments/i.test(message)) {
    return '请执行 supabase/migrations/246_crm_task_attachments.sql（任务附件 Storage）。';
  }
  if (/project_attachments/i.test(message)) {
    return '请执行 supabase/migrations/005_crm_storage_project_attachments.sql（项目附件 Storage）。';
  }
  if (context === 'storage' || /client_logos/i.test(message)) {
    return '请执行 supabase/migrations/004_crm_storage_client_logos.sql（Logo Storage）。';
  }
  return '请执行 supabase/migrations/001_crm_web_rls.sql（表 RLS）；附件另需 005，Logo 另需 004。';
}

export function formatSupabaseError(
  error: unknown,
  context?: 'storage' | 'crm' | 'attachment',
): string {
  if (!error || typeof error !== 'object') {
    return 'Operation failed. Please try again.';
  }

  const pg = error as PostgrestError;
  const message = pg.message ?? (error instanceof Error ? error.message : String(error));
  const code = pg.code ?? '';

  if (/FORMS_SUBMIT_FORBIDDEN/i.test(message)) {
    return (
      'Submit not allowed for your Forms account. Ask IT to assign a department on Application Form access ' +
      '(Admin Hub → Portal users), then sign out and back in.'
    );
  }

  if (/relation "public\.users" does not exist|relation public\.users does not exist/i.test(message)) {
    return (
      `${message} — 数据库已移除 public.users 表，但账户转移等函数仍在引用它。请在 Supabase SQL Editor 运行 033_account_transfer_no_users.sql，` +
      "然后执行 NOTIFY pgrst, 'reload schema';，等待约 10 秒后刷新并重试。若从未合并用户表，可先运行 014_merge_users_into_allowed_emails.sql。"
    );
  }

  if (/log_account_activity_event/i.test(message)) {
    return (
      `${message} — 请在 Supabase SQL Editor 运行 \`032_log_account_activity_event.sql\`（若从未跑过活动日志，先运行 \`011_account_transfers_notifications.sql\`），` +
      '然后执行 `NOTIFY pgrst, \'reload schema\';`，等待约 10 秒后刷新页面重试。'
    );
  }

  if (
    /Could not find the function .*upsert_portal_user_profile/i.test(message) ||
    /Could not find the table .*portal_user_profiles/i.test(message) ||
    /relation .*portal_user_profiles.* does not exist/i.test(message)
  ) {
    return (
      `${message} — 请在 Supabase SQL Editor 运行 \`supabase/migrations/082_portal_user_profiles.sql\`，` +
      "然后执行 `NOTIFY pgrst, 'reload schema';`，等待约 10 秒后刷新页面重试。"
    );
  }

  if (/Could not find the function/i.test(message)) {
    return (
      `${message} — 对应数据库函数尚未部署，或 PostgREST schema 缓存未刷新。` +
      "请在 SQL Editor 运行相关 migration，再执行 `NOTIFY pgrst, 'reload schema';`。"
    );
  }

  if (
    /Could not find the table ['"]?it\.monthly_visitation_items/i.test(message) ||
    /relation ['"]?it\.monthly_visitation_items['"]? does not exist/i.test(message)
  ) {
    return (
      `${message} — IT 门户缺少月度拜访表。请在 Supabase SQL Editor 运行 \`supabase/migrations/107_it_monthly_visitation_items.sql\`，` +
      "然后执行 `NOTIFY pgrst, 'reload schema';`，等待约 10 秒后刷新页面。"
    );
  }

  if (
    /Could not find the table ['"]?crm\.monthly_visitation_items/i.test(message) ||
    /relation ['"]?crm\.monthly_visitation_items['"]? does not exist/i.test(message)
  ) {
    return (
      `${message} — 请在 Supabase SQL Editor 运行 \`090_monthly_visitation_list.sql\` 与 \`099_monthly_visitation_prospects.sql\`，` +
      "然后执行 `NOTIFY pgrst, 'reload schema';`，等待约 10 秒后刷新页面。"
    );
  }

  if (
    code === 'PGRST204' ||
    /schema cache|schema is out of sync|out of sync/i.test(message) ||
    /logo_path/i.test(message) ||
    /Could not find the .* column.*clients/i.test(message) ||
    /crm_set_client_logo_path/i.test(message)
  ) {
    if (/Could not find the table ['"]?public\.crm_allowed_emails/i.test(message)) {
      return (
        `${message} — 数据库表在 crm schema，但网页仍在查 public。请部署启用 VITE_SUPABASE_DB_SCHEMA=crm 的前端，并在 Supabase 运行 034_crm_schema_web_setup.sql、暴露 crm schema。详见 docs/CRM-SCHEMA-zh.md`
      );
    }
    if (/Could not find the table ['"]?it\./i.test(message)) {
      return (
        `${message} — IT schema 表缺失或未暴露。请确认 Dashboard → API → Exposed schemas 包含 it，运行对应 migration，然后 NOTIFY pgrst, 'reload schema';。`
      );
    }
    if (/Could not find the table ['"]?crm\./i.test(message)) {
      return (
        `${message} — 请在 Supabase SQL Editor 运行 034_crm_schema_web_setup.sql，Dashboard → API → Exposed schemas 添加 crm，然后 NOTIFY pgrst, 'reload schema';。详见 docs/CRM-SCHEMA-zh.md`
      );
    }
    if (/schema is out of sync|Please run migrations or contact support/i.test(message)) {
      return (
        'Supabase API 尚未识别新字段（schema 缓存过期）。请在 SQL Editor 运行 `025_postgrest_reload_and_logo_rpc.sql`，' +
        "等待约 10 秒后执行 `NOTIFY pgrst, 'reload schema';`，再刷新网页重试。" +
        '若仍失败：Dashboard → Project Settings → 重启项目。详见 docs/CLIENT-LOGO-zh.md'
      );
    }
    if (/logo_path|crm_set_client_logo_path|Could not find the .* column.*clients/i.test(message)) {
      return (
        `${message} — 请在 Supabase SQL Editor 运行 \`024_clients_logo_path.sql\` 与 \`025_postgrest_reload_and_logo_rpc.sql\`，然后执行 NOTIFY pgrst, 'reload schema';。详见 docs/CLIENT-LOGO-zh.md`
      );
    }
    return (
      `${message} — PostgREST schema 缓存过期或对象未部署。请运行相关 migration，再执行 \`NOTIFY pgrst, 'reload schema';\`，等待约 10 秒后刷新。`
    );
  }

  if (
    code === '42501' ||
    /row-level security/i.test(message) ||
    /permission denied/i.test(message)
  ) {
    const hint = rlsHint(message, context);
    return `${message} — ${hint} 详见 docs/APPLY-RLS-zh.md`;
  }

  if (
    /update or delete on table ["']?crm_allowed_emails["']?/i.test(message) &&
    /clients_owner_id_fkey|contacts_owner_id_fkey|projects_owner_id_fkey|tasks_owner_id_fkey|project_updates_author_id_fkey/i.test(
      message,
    )
  ) {
    return (
      'This user still has accounts. ' +
      'Transfer the accounts to another owner first, then change CRM access again.'
    );
  }

  if (/account_activity_log_actor_id_fkey|account_activity_log.*actor_id/i.test(message)) {
    return (
      'Your profile could not be linked to the activity timeline. ' +
      'The account changes are still saved. Try signing out and back in; if it keeps happening, ask IT to sync your login to the allowlist.'
    );
  }

  if (
    /account_transfer_requests_requested_by_fkey|account_transfer_requests_reviewed_by_fkey|requested_by_fkey|reviewed_by_fkey/i.test(
      message,
    )
  ) {
    return (
      'Transfer could not be saved because your login is not linked to the CRM allowlist. ' +
      'Ask IT to run migration 092 (or add your email), then sign out and sign in again.'
    );
  }

  if (/Your login is not linked to the CRM allowlist/i.test(message)) {
    return message;
  }

  if (/Account code cannot be changed once a permanent/i.test(message)) {
    return 'This account already has a permanent customer code. It cannot be changed here.';
  }

  if (/Enter a permanent customer code/i.test(message)) {
    return 'Enter the permanent customer code from Finance / NAV (not a TEMP code).';
  }

  if (/Not authorized to assign account code/i.test(message)) {
    return 'You do not have permission to assign the customer code for this account.';
  }

  if (/clients_code_lower_unique|duplicate key.*clients.*code/i.test(message)) {
    return 'This customer code is already used by another account. Choose a different code.';
  }

  if (/clients_owner_id_fkey/i.test(message)) {
    return 'The account owner is invalid. Refresh the page and try again, or ask IT to check the owner on the allowlist.';
  }

  if (/(contacts|projects|tasks)_owner_id_fkey|foreign key constraint.*(contacts|projects|tasks).*owner_id/i.test(message)) {
    return (
      'Related contacts, projects, or tasks could not be reassigned because the target salesperson code ' +
      'is not assigned to a CRM user yet. The account can still move to that code — try approving again, ' +
      'or assign the code in User management first if you also need contacts/projects/tasks moved.'
    );
  }

  if (/foreign key constraint.*owner_id/i.test(message)) {
    return 'The owner could not be updated. Refresh and try again, or ask IT to check salesperson code assignment.';
  }

  if (/project_updates_author_id_fkey/i.test(message)) {
    return 'Your login is not on the CRM allowlist, so this action could not be recorded. Sign out, sign back in, and try again.';
  }

  if (/foreign key constraint/i.test(message)) {
    return 'This change could not be linked to related records. The main save may still have worked — refresh and check, or contact IT if it persists.';
  }

  if (/duplicate key.*memo_submissions_application_no|memo_submissions_application_no_key/i.test(message)) {
    return 'This memorandum Ref no is already used. Refresh and save again — a new number will be assigned.';
  }

  if (/duplicate key|unique constraint/i.test(message)) {
    return 'This value is already in use. Please use a different one.';
  }

  if (/violates check constraint/i.test(message)) {
    return 'One of the values is not allowed. Please check the form and try again.';
  }

  if (/Not authenticated/i.test(message)) {
    return 'Your session expired. Please sign in again.';
  }

  if (/Not authorized/i.test(message)) {
    return 'You do not have permission to do this.';
  }

  // Prefer short plain English when we only have a raw Postgres/PostgREST blob.
  if (/violates foreign key|insert or update on table/i.test(message)) {
    return 'Something could not be linked in the database. Your main change may still be saved — refresh to confirm.';
  }

  return message;
}
